import type { ApiResponse, TokenResponse } from './types';

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8180';

const ACCESS_TOKEN_KEY = 'auto-threads.accessToken';
const REFRESH_TOKEN_KEY = 'auto-threads.refreshToken';
const ACCESS_EXPIRES_KEY = 'auto-threads.accessTokenExpiredAt';
const REFRESH_EXPIRES_KEY = 'auto-threads.refreshTokenExpiredAt';

/** 만료 판정 시 여유 시간(ms). 실제 만료 30초 전부터 갱신 대상으로 본다. */
const EXPIRY_SKEW_MS = 30_000;

function toEpoch(value?: string | null): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  getAccessTokenExpiry: () => toEpoch(localStorage.getItem(ACCESS_EXPIRES_KEY)),
  getRefreshTokenExpiry: () =>
    toEpoch(localStorage.getItem(REFRESH_EXPIRES_KEY)),
  set: (token: TokenResponse) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, token.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, token.refreshToken);
    if (token.accessTokenExpiredAt) {
      localStorage.setItem(ACCESS_EXPIRES_KEY, token.accessTokenExpiredAt);
    }
    if (token.refreshTokenExpiredAt) {
      localStorage.setItem(REFRESH_EXPIRES_KEY, token.refreshTokenExpiredAt);
    }
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACCESS_EXPIRES_KEY);
    localStorage.removeItem(REFRESH_EXPIRES_KEY);
  },
  /** access token이 만료됐거나 곧 만료되는지 */
  isAccessTokenExpired: () => {
    const expiry = toEpoch(localStorage.getItem(ACCESS_EXPIRES_KEY));
    if (expiry == null) return false; // 만료 정보가 없으면 반응형(401) 경로에 맡긴다
    return Date.now() >= expiry - EXPIRY_SKEW_MS;
  },
  /** refresh token 자체가 만료됐는지 */
  isRefreshTokenExpired: () => {
    const expiry = toEpoch(localStorage.getItem(REFRESH_EXPIRES_KEY));
    if (expiry == null) return false;
    return Date.now() >= expiry;
  },
};

export class ApiError extends Error {
  code: number;
  description?: string;

  constructor(message: string, code: number, description?: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.description = description;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
  /** 401 발생 시 refresh 후 1회 재시도할지 여부 (내부용) */
  _retry?: boolean;
}

let onUnauthorized: (() => void) | null = null;

/** 인증 만료(재로그인 필요) 시 호출할 콜백 등록 */
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

async function parseResponse<T>(res: Response): Promise<ApiResponse<T>> {
  const text = await res.text();
  if (!text) {
    return {
      result: {
        resultCode: res.status,
        resultMessage: res.statusText,
        resultDescription: '',
      },
      body: undefined as unknown as T,
    };
  }
  try {
    return JSON.parse(text) as ApiResponse<T>;
  } catch {
    return {
      result: {
        resultCode: res.status,
        resultMessage: res.statusText,
        resultDescription: text,
      },
      body: undefined as unknown as T,
    };
  }
}

/** 동시 요청이 각각 refresh를 호출하지 않도록 단일 갱신(single-flight)으로 묶는다. */
let refreshPromise: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return false;
  if (tokenStorage.isRefreshTokenExpired()) return false;

  try {
    const res = await fetch(`${API_BASE_URL}/open-api/users/refresh`, {
      method: 'GET',
      headers: { Authorization: refreshToken },
    });
    if (!res.ok) return false;
    const parsed = await parseResponse<TokenResponse>(res);
    if (parsed.body?.accessToken) {
      tokenStorage.set(parsed.body);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function tryRefreshToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * 백엔드는 토큰 관련 오류를 HTTP 400 + result.resultCode(2000/2001/2404/2505)로 내려준다.
 * (INVALID_TOKEN=2000, EXPIRED_TOKEN=2001, AUTHORIZATION_TOKEN_NOT_FOUND=2404, TOKEN_EXCEPTION=2505)
 * 따라서 401/403뿐 아니라 이 코드들도 "인증 만료 → refresh 재시도" 대상으로 본다.
 */
const TOKEN_ERROR_CODES = new Set([2000, 2001, 2404, 2505]);

function isAuthExpiredResponse(
  status: number,
  parsed: ApiResponse<unknown>
): boolean {
  if (status === 401) return true;
  if (status === 400 || status === 403) {
    const code = parsed.result?.resultCode;
    return typeof code === 'number' && TOKEN_ERROR_CODES.has(code);
  }
  return false;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, auth = true, headers = {}, _retry } = options;

  const finalHeaders: Record<string, string> = { ...headers };
  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (auth) {
    // 선제적 갱신: access token이 만료(임박)됐고 refresh가 유효하면 먼저 재발급
    if (
      !_retry &&
      tokenStorage.getAccessToken() &&
      tokenStorage.isAccessTokenExpired() &&
      !tokenStorage.isRefreshTokenExpired()
    ) {
      await tryRefreshToken();
    }
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken) {
      finalHeaders['Authorization'] = accessToken;
    }
  }

  const url = `${API_BASE_URL}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: finalHeaders,
      credentials: options.credentials,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    // CORS/네트워크 실패는 ApiError가 아니라 TypeError로 떨어진다.
    // 로그인 화면에서 "비밀번호 오류"처럼 보이지 않도록 원인 URL을 명시한다.
    throw new ApiError(
      `서버에 연결할 수 없습니다. (${API_BASE_URL})`,
      0,
      err instanceof Error ? err.message : String(err)
    );
  }

  const parsed = await parseResponse<T>(res);

  // 인증 만료(401/403 또는 400+토큰 에러코드) → refresh 후 새 access token으로 1회 재시도
  if (auth && !_retry && isAuthExpiredResponse(res.status, parsed)) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      return request<T>(path, { ...options, _retry: true });
    }
    tokenStorage.clear();
    onUnauthorized?.();
    throw new ApiError('세션이 만료되었습니다. 다시 로그인해 주세요.', res.status);
  }

  if (!res.ok) {
    const { resultMessage, resultDescription, resultCode } = parsed.result ?? {
      resultMessage: '요청에 실패했습니다.',
      resultDescription: '',
      resultCode: res.status,
    };
    throw new ApiError(
      res.status === 403 ? '이 기능은 워크스페이스 관리자만 사용할 수 있습니다.' : resultMessage || '요청에 실패했습니다.',
      resultCode ?? res.status,
      resultDescription
    );
  }

  return parsed.body;
}

/** multipart/form-data 업로드 (Content-Type은 브라우저가 boundary 포함해 설정) */
export async function requestFormData<T>(
  path: string,
  formData: FormData,
  options: { method?: 'POST' | 'PUT' | 'PATCH'; auth?: boolean; _retry?: boolean } = {}
): Promise<T> {
  const { method = 'POST', auth = true, _retry } = options;
  const finalHeaders: Record<string, string> = {};

  if (auth) {
    if (
      !_retry &&
      tokenStorage.getAccessToken() &&
      tokenStorage.isAccessTokenExpired() &&
      !tokenStorage.isRefreshTokenExpired()
    ) {
      await tryRefreshToken();
    }
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken) {
      finalHeaders.Authorization = accessToken;
    }
  }

  const url = `${API_BASE_URL}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: finalHeaders,
      body: formData,
    });
  } catch (err) {
    throw new ApiError(
      `서버에 연결할 수 없습니다. (${API_BASE_URL})`,
      0,
      err instanceof Error ? err.message : String(err)
    );
  }

  const parsed = await parseResponse<T>(res);

  if (auth && !_retry && isAuthExpiredResponse(res.status, parsed)) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      return requestFormData<T>(path, formData, { ...options, _retry: true });
    }
    tokenStorage.clear();
    onUnauthorized?.();
    throw new ApiError('세션이 만료되었습니다. 다시 로그인해 주세요.', res.status);
  }

  if (!res.ok) {
    const { resultMessage, resultDescription, resultCode } = parsed.result ?? {
      resultMessage: '요청에 실패했습니다.',
      resultDescription: '',
      resultCode: res.status,
    };
    throw new ApiError(
      res.status === 403 ? '이 기능은 워크스페이스 관리자만 사용할 수 있습니다.' : resultMessage || '요청에 실패했습니다.',
      resultCode ?? res.status,
      resultDescription
    );
  }

  return parsed.body;
}
